// Web Push enable/disable for the PWA. The browser-side PushManager work lives
// here; the server half (storing/clearing the subscription) goes through the
// bridge (`register_push` / `unregister_push`), per the "components reach the
// server only through the bridge" rule.
//
// Permission is requested ONLY from an explicit user gesture (the Settings
// toggle) — never auto-prompted.

import { invoke } from "./bridge";

/** VAPID public key, injected as a meta tag by the Rails layout (CSP forbids
 *  inline <script>). Empty when the server has no VAPID keys configured. */
function vapidPublicKey(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="vapid-public-key"]')?.content ?? "";
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(normalized);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Whether this browser can do Web Push at all, and the server is configured. */
export function isPushSupported(): boolean {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window &&
    vapidPublicKey() !== ""
  );
}

/** True if this device already has an active push subscription. */
export async function isPushEnabled(): Promise<boolean> {
  if (!isPushSupported()) return false;
  const reg = await navigator.serviceWorker.ready;
  return (await reg.pushManager.getSubscription()) !== null;
}

/** Request permission, subscribe, and register the subscription with the server.
 *  Returns true on success. Throws only on unexpected errors; a denied
 *  permission resolves to false. */
export async function enablePush(): Promise<boolean> {
  if (!isPushSupported()) return false;

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return false;

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey()),
    });
  }

  await invoke("register_push", { subscription: sub.toJSON() });
  return true;
}

/** Unsubscribe locally and clear the subscription on the server. */
export async function disablePush(): Promise<void> {
  try {
    await invoke("unregister_push");
  } catch {
    // best-effort; still unsubscribe locally below
  }
  if (!("serviceWorker" in navigator)) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) await sub.unsubscribe();
}
